/**
 * @fileoverview Custom hook and component for detecting if an element is in the viewport.
 */

import React, {ReactNode, useEffect, useRef, useState} from "react";
import {useIntersection} from "react-use";

/**
 * Custom hook that detects if an element is in the viewport.
 * Uses intersection observer to track when the element becomes fully visible.
 *
 * @returns {Object} Object containing the ref and viewport state.
 * @returns {RefObject} returns.ref Ref to attach to the element to observe.
 * @returns {boolean} returns.wasInViewport Whether the element has been in the viewport.
 */
export const useIsInViewport = () => {
  const [wasInViewport, setWasInViewport] = useState(false);
  const ref = useRef(null);

  const {intersectionRatio = 0} =
    useIntersection(ref, {
      // TODO: parametrize
      root: document.querySelector(".js-reporting-scroll-container"),
      rootMargin: "0px",
      threshold: 1,
    }) ?? {};

  useEffect(() => {
    if (intersectionRatio === 1) {
      setWasInViewport(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intersectionRatio]);

  return {ref, wasInViewport};
};

/**
 * Component that provides viewport detection through a render prop pattern.
 * Uses the useIsInViewport hook internally and passes the result to children.
 *
 * @param {Object} props The component props.
 * @param {Function} props.children Render prop function that receives the hook result.
 * @returns {JSX.Element} The rendered children with hook result.
 */
export const UseIsInViewport = ({
  children,
}: {
  children: (props: ReturnType<typeof useIsInViewport>) => ReactNode;
}) => <>{children(useIsInViewport())} </>;
